import { useEffect, useRef } from 'react';

interface UseFocusTrapOptions {
  isActive: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  restoreFocus?: HTMLElement | null;
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, video[controls], [tabindex]:not([tabindex="-1"])';

/** Traps Tab focus inside the returned container ref while active, restoring prior focus on release. */
export const useFocusTrap = ({ isActive, initialFocusRef, restoreFocus }: UseFocusTrapOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    previousActiveElement.current = document.activeElement as HTMLElement;

    const focusInitialElement = () => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else if (containerRef.current) {
        const firstFocusable = containerRef.current.querySelector(
          FOCUSABLE_SELECTOR
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    };

    // Small delay to ensure modal is rendered
    const focusTimer = window.setTimeout(focusInitialElement, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !containerRef.current) {
        return;
      }

      const focusableElements = containerRef.current.querySelectorAll(
        FOCUSABLE_SELECTOR
      ) as NodeListOf<HTMLElement>;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);

      if (restoreFocus) {
        restoreFocus.focus();
      } else if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, initialFocusRef, restoreFocus]);

  return containerRef;
};
